import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Calculator, HeartPulse, Landmark, Scale, Package,
  Sparkles, BookOpen, Loader2, CheckCircle2, Search, X,
  ChevronLeft, ChevronRight, type LucideIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState } from "@/components/common";
import { useApp } from "@/lib/store";

interface Template {
  slug: string;
  command: string;
  title: string;
  industry: string;
  icon: string;
  tagline: string;
  ontology_basis: string[];
  entities: number;
  relations: number;
  sample_docs: number;
  agents: number;
  estimated_install_seconds: number;
  onboarding_slug: string;
  status: string;
  // Annotated by BE when ?workspace_id=<id> is supplied so the FE
  // can disable double-installs and show a clear badge.
  installed?: boolean;
  install_count?: number;
  last_installed_at?: string | null;
}

const ICONS: Record<string, LucideIcon> = {
  Calculator, HeartPulse, Landmark, Scale, Package, Sparkles,
};

export function Marketplace() {
  const ws = useApp((s) => s.workspaceId);
  const qc = useQueryClient();
  const [installing, setInstalling] = useState<string | null>(null);
  const [installLog, setInstallLog] = useState<{ slug: string; log: string; counts?: Record<string, number> } | null>(null);

  // Search + filter + pagination state.
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 6;

  const templates = useQuery<{ results: Template[] }>({
    // Key on workspace so switching the top-bar selector refreshes the
    // "installed" annotation on every card.
    queryKey: ["marketplace-templates", ws],
    queryFn: () => rawFetch(`/packs/marketplace/templates/${ws ? `?workspace_id=${ws}` : ""}`),
  });

  const all = templates.data?.results ?? [];
  const industries = useMemo(
    () => Array.from(new Set(all.map((t) => t.industry))).sort(),
    [all]
  );
  const statuses = useMemo(
    () => Array.from(new Set(all.map((t) => t.status))).sort(),
    [all]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((t) => {
      if (industry !== "all" && t.industry !== industry) return false;
      if (status !== "all" && t.status !== status) return false;
      if (!needle) return true;
      const hay = [
        t.title, t.tagline, t.industry, t.slug,
        ...(t.ontology_basis ?? []),
      ].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [all, q, industry, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const install = useMutation({
    mutationFn: async (slug: string) => {
      setInstalling(slug);
      try {
        return await rawFetch(`/packs/marketplace/templates/${slug}/install/`, {
          method: "POST",
          body: JSON.stringify({ workspace_id: ws }),
        });
      } finally {
        setInstalling(null);
      }
    },
    onSuccess: (data: any, slug) => {
      setInstallLog({ slug, log: data?.log ?? "", counts: data?.counts });
      // Nuke every cached query so any page the user lands on shows fresh data.
      qc.invalidateQueries();
    },
  });

  const resetFilters = () => { setQ(""); setIndustry("all"); setStatus("all"); setPage(1); };
  const filtersActive = q !== "" || industry !== "all" || status !== "all";

  return (
    <>
      <PageHeader
        title="Industry Marketplace"
        subtitle="One-click starter packs by vertical — ontology, sample corpus, retrieval profile, agents, evals, and an ingest pipeline. Re-installing is safe (idempotent)."
        help={
          <>
            Each template runs <code>manage.py seed_&lt;slug&gt;</code> against the
            currently selected workspace's tenant schema. Read the onboarding doc
            for the engineering migration playbook before going to production.
          </>
        }
      />

      {/* Differentiation banner — users were confused about Marketplace vs Packs vs Ontology Standards. */}
      <div className="card mb-4 border-accent/30 text-xs text-muted">
        <div className="mb-1 text-sm font-semibold text-fg">Marketplace vs Packs vs Ontology Standards</div>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong className="text-fg">Marketplace (here):</strong> turn-key <em>vertical starters</em> &mdash; ontology + sample corpus + retrieval profile + agents + evals + a pre-wired pipeline. Pick a vertical, install, you have a working workspace in seconds. <em>Re-installing is idempotent.</em></li>
          <li><strong className="text-fg"><Link to="/packs" className="text-accent underline">Packs</Link>:</strong> <em>signed, versioned bundles</em> you build from any workspace and ship to other tenants / customers. Same payload shape as a marketplace install, but signed and registry-distributable.</li>
          <li><strong className="text-fg"><Link to="/ontology" className="text-accent underline">Ontology &rarr; Standards</Link>:</strong> reference or align a workspace schema to a supported external source. Import is source-specific and stays disabled until licence, provenance, version, and redistribution gates pass; ACORD and restricted clinical terminologies are never bundled by Ontoligent.</li>
        </ul>
        <div className="mt-2 text-[11px]">They <em>compose</em>: install a marketplace template, then seed an additional standard on top, then build a pack from the result.</div>
        <div className="mt-2 text-[11px] text-warn">
          <strong className="text-fg">Heads-up on "FIBO-aligned" / "ACORD-aligned" / "FHIR-aligned" wording on Marketplace cards:</strong>{" "}
          alignment means the workspace-authored entity types (Policy, LegalEntity, Coverage, Premium&hellip;) use explicit mappings to selected external terms. It does <strong>not</strong> imply that source ontology content is bundled or redistributable. Composer step&nbsp;4 records the selected source, version, mode, rights evidence, and attestation; hard rights gates remain authoritative regardless of a recommendation score.
        </div>
      </div>

      {!ws && (
        <div className="card mb-4 border-amber-500/40 text-sm">
          Select a workspace from the header dropdown before installing a template.
          Until you do, the cards below cannot show which packs are already installed.
        </div>
      )}
      {ws && (
        <div className="card mb-4 flex items-center justify-between border-accent/30 text-xs text-muted">
          <div>
            Showing install state for workspace <code className="font-mono text-fg">{String(ws).slice(0, 8)}…</code>. Switch via the header dropdown to check another tenant.
          </div>
          <Link to="/packs" className="text-accent underline">See all installs &rarr;</Link>
        </div>
      )}

      {/* --- Search + filter toolbar --- */}
      <div className="card mb-4 flex flex-col gap-2 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search size={14}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input
            className="input pl-8"
            placeholder="Search templates by name, industry, or ontology basis…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
          />
        </div>
        <select
          className="input h-8 w-full py-0 md:w-[180px]"
          value={industry}
          onChange={(e) => { setIndustry(e.target.value); setPage(1); }}
          aria-label="Filter by industry"
        >
          <option value="all">All industries</option>
          {industries.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select
          className="input h-8 w-full py-0 md:w-[140px]"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {filtersActive && (
          <button className="btn" onClick={resetFilters} title="Clear filters">
            <X size={14} /> Clear
          </button>
        )}
      </div>

      <div className="mb-2 flex items-center justify-between text-xs text-muted">
        <div>
          {filtered.length === 0
            ? "No templates match."
            : `Showing ${pageStart + 1}–${Math.min(pageStart + PAGE_SIZE, filtered.length)} of ${filtered.length}`}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              className="btn-ghost"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="px-1 text-muted">Page {safePage} / {totalPages}</span>
            <button
              className="btn-ghost"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {templates.isError && <ErrorBox error={templates.error} />}
      {templates.isLoading && (
        <div className="card text-muted text-sm">Loading templates…</div>
      )}
      {!templates.isLoading && filtered.length === 0 && (
        <EmptyState title="No templates match your filters."
                    hint="Try clearing the search or pick a different industry." />
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {pageItems.map((t) => {
          const Icon = ICONS[t.icon] ?? Package;
          const isBusy = install.isPending && installing === t.slug;
          const isDone = installLog?.slug === t.slug;
          const alreadyInstalled = !!t.installed;
          return (
            <div key={t.slug} className={`card flex flex-col gap-3 ${alreadyInstalled ? "border-good/40" : ""}`}>
              <div className="flex items-start gap-3">
                <div className="rounded-md border border-border bg-bg p-2 text-accent">
                  <Icon size={22} />
                </div>
                <div className="flex-1">
                  <div className="text-base font-semibold leading-tight">{t.title}</div>
                  <div className="mt-0.5 text-xs uppercase tracking-wide text-muted">
                    {t.industry}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="pill-good text-[10px]">{t.status}</span>
                  {alreadyInstalled && (
                    <span
                      className="rounded-full border border-good/50 px-2 py-0.5 text-[10px] text-good"
                      title={t.last_installed_at ? `Last installed ${new Date(t.last_installed_at).toLocaleString()}` : "Already installed in this workspace"}
                    >
                      Installed{(t.install_count ?? 0) > 1 ? ` ×${t.install_count}` : ""}
                    </span>
                  )}
                </div>
              </div>

              <p className="text-sm text-muted">{t.tagline}</p>

              <div className="flex flex-wrap gap-1">
                {t.ontology_basis.slice(0, 5).map((b) => (
                  <span key={b}
                        className="rounded border border-border bg-bg/60 px-1.5 py-0.5 text-[10px] text-muted">
                    {b}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <Stat label="entities"  value={t.entities} />
                <Stat label="relations" value={t.relations} />
                <Stat label="docs"      value={t.sample_docs} />
                <Stat label="agents"    value={t.agents} />
              </div>

              <div className="mt-auto flex items-center gap-2 pt-1">
                <button
                  className={alreadyInstalled ? "btn inline-flex items-center gap-1" : "btn-primary inline-flex items-center gap-1"}
                  disabled={!ws || install.isPending}
                  onClick={() => install.mutate(t.slug)}
                  title={!ws ? "Pick a workspace first"
                    : alreadyInstalled ? `Already installed in this workspace${t.last_installed_at ? " on " + new Date(t.last_installed_at).toLocaleDateString() : ""}. Re-installing is idempotent and will refresh the seed.`
                    : "Install into the current workspace"}
                >
                  {isBusy ? <Loader2 size={14} className="animate-spin" /> :
                   (isDone || alreadyInstalled) ? <CheckCircle2 size={14} /> : <Sparkles size={14} />}
                  {isBusy ? "Installing…" : isDone ? "Installed" : alreadyInstalled ? "Reinstall" : "Install"}
                </button>
                <Link to="/onboarding/$slug" params={{ slug: t.onboarding_slug }}
                      className="btn inline-flex items-center gap-1">
                  <BookOpen size={14} /> Onboarding
                </Link>
                <span className="ml-auto text-[10px] text-muted">
                  ~{t.estimated_install_seconds}s
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {install.isError && (
        <div className="mt-4">
          <ErrorBox error={install.error} />
        </div>
      )}

      {installLog && (
        <div className="card mt-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs uppercase text-muted">
              Install result — {installLog.slug}
            </div>
            <div className="flex gap-2">
              <Link to="/agents" className="btn">View agents</Link>
              <Link to="/catalog" className="btn">View catalog</Link>
              <Link to="/retrieval" className="btn">View retrieval</Link>
              <Link to="/pipelines" className="btn">View pipelines</Link>
              <Link to="/evals" className="btn">View evals</Link>
            </div>
          </div>
          {installLog.counts && (
            <div className="mb-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-7">
              {Object.entries(installLog.counts).map(([k, v]) => (
                <div key={k} className="rounded border border-border bg-bg/60 px-2 py-1.5 text-center">
                  <div className="text-base font-semibold">{v}</div>
                  <div className="text-[10px] uppercase text-muted">{k.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
          )}
          <pre className="max-h-60 overflow-auto rounded border border-border bg-bg p-2 text-xs">
            {installLog.log || "(empty)"}
          </pre>
        </div>
      )}

      <OntologyPacksSection />
    </>
  );
}

interface MarketplacePack {
  name: string; semver: string; kind: string; tier: number; universal: boolean;
  applicable_industries: string[]; jurisdiction: string; languages: string[];
}

/** Phase 6.4 — tiered ontology-pack catalogue (Phase 4.4's BE filters),
 *  separate from the industry-template starters above. Tier badges +
 *  kind/tier/universal/industry filters, plus a "Compose from these"
 *  bulk action that hands the current filter off to the Composer wizard. */
function OntologyPacksSection() {
  const [kind, setKind] = useState<string>("all");
  const [tier, setTier] = useState<string>("all");
  const [universalOnly, setUniversalOnly] = useState(false);
  const [industry, setIndustry] = useState("");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (kind !== "all") p.set("kind", kind);
    if (tier !== "all") p.set("tier", tier);
    if (universalOnly) p.set("universal", "true");
    if (industry.trim()) p.set("industry", industry.trim());
    return p.toString();
  }, [kind, tier, universalOnly, industry]);

  const packsQ = useQuery<{ results: MarketplacePack[] }>({
    queryKey: ["marketplace-packs", params],
    queryFn: () => rawFetch(`/packs/marketplace/packs/${params ? `?${params}` : ""}`),
  });

  const packs = packsQ.data?.results ?? [];

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Ontology Packs</h2>
          <p className="text-xs text-muted">
            Tiered building blocks (Foundation / Core Domain / Industry / Organization / …). Filter and
            hand a set off to the Composer wizard.
          </p>
        </div>
        <Link to="/studio/composer" className="btn-primary inline-flex items-center gap-1">
          <Sparkles size={14} /> Compose from these
        </Link>
      </div>

      <div className="card mb-3 flex flex-col gap-2 md:flex-row md:items-center">
        <select className="input h-8 w-full py-0 md:w-[160px]" value={kind}
                onChange={(e) => setKind(e.target.value)} aria-label="Filter by kind">
          <option value="all">All kinds</option>
          <option value="FOUNDATION">Foundation</option>
          <option value="DOMAIN">Domain</option>
          <option value="INDUSTRY">Industry</option>
          <option value="LOB">LOB</option>
          <option value="REGIONAL">Regional</option>
          <option value="CUSTOMER">Customer</option>
          <option value="BUNDLE">Bundle</option>
        </select>
        <select className="input h-8 w-full py-0 md:w-[140px]" value={tier}
                onChange={(e) => setTier(e.target.value)} aria-label="Filter by tier">
          <option value="all">All tiers</option>
          {[0, 1, 2, 3, 4, 5, 6].map((t) => <option key={t} value={t}>Tier {t}</option>)}
        </select>
        <input
          className="input h-8 flex-1 py-0" placeholder="Industry (e.g. insurance, banking)"
          value={industry} onChange={(e) => setIndustry(e.target.value)}
        />
        <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted">
          <input type="checkbox" checked={universalOnly}
                 onChange={(e) => setUniversalOnly(e.target.checked)} />
          Universal only
        </label>
      </div>

      {packsQ.isError && <ErrorBox error={packsQ.error} />}
      {packsQ.isLoading && <div className="card text-sm text-muted">Loading packs…</div>}
      {!packsQ.isLoading && packs.length === 0 && (
        <EmptyState title="No packs match these filters." />
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {packs.map((p) => (
          <div key={`${p.name}@${p.semver}`} className="card">
            <div className="flex items-center justify-between">
              <div className="font-medium">{p.name}</div>
              <span className="text-xs text-muted">v{p.semver}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              <span className="pill">{p.kind}</span>
              <span className="pill">Tier {p.tier}</span>
              {p.universal && <span className="pill-good">universal</span>}
              {p.applicable_industries.map((i) => <span key={i} className="pill">{i}</span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-border bg-bg/60 px-1 py-1.5">
      <div className="text-sm font-semibold">{value}</div>
      <div className="text-[10px] uppercase text-muted">{label}</div>
    </div>
  );
}
